import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Table, Spinner, Alert, Badge, Form, ProgressBar
} from 'react-bootstrap';
import { PeopleFill, PersonPlusFill, PersonCheckFill, PrinterFill, FunnelFill, GenderMale, GenderFemale } from 'react-bootstrap-icons';
import { Button } from 'react-bootstrap';
import { secureApiEndpoints } from '../../utils/apiMigration';

function StatistiquesCycle() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSection, setSelectedSection] = useState('all');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await secureApiEndpoints.students.enrollmentStatsByCycle();
      if (response.success) {
        setData(response.data);
      } else {
        setError(response.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Chargement des statistiques...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!data) return null;

  const { by_section, sections, global, school_year } = data;

  // Filtrer par section
  const filteredSections = selectedSection === 'all'
    ? by_section
    : by_section.filter(s => String(s.section_id) === selectedSection);

  // Calculer les totaux filtrés
  const filteredGlobal = selectedSection === 'all'
    ? global
    : filteredSections.reduce((acc, s) => ({
        nouveaux: acc.nouveaux + s.totals.nouveaux,
        anciens: acc.anciens + s.totals.anciens,
        garcons: acc.garcons + s.totals.garcons,
        filles: acc.filles + s.totals.filles,
        total: acc.total + s.totals.total,
      }), { nouveaux: 0, anciens: 0, garcons: 0, filles: 0, total: 0 });

  const pctNouveaux = filteredGlobal.total > 0 ? ((filteredGlobal.nouveaux / filteredGlobal.total) * 100).toFixed(1) : 0;
  const pctAnciens = filteredGlobal.total > 0 ? ((filteredGlobal.anciens / filteredGlobal.total) * 100).toFixed(1) : 0;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const today = new Date().toLocaleDateString('fr-FR');
    const filterLabel = selectedSection === 'all'
      ? 'Tous les cycles'
      : filteredSections[0]?.section_name || '';

    let tableRows = '';
    filteredSections.forEach(section => {
      // Section header
      tableRows += `<tr class="section-row"><td colspan="7" style="background:#2c3e50;color:white;font-weight:bold;padding:6px 10px;font-size:11pt;">${section.section_name}</td></tr>`;

      section.levels.forEach(level => {
        // Level header
        tableRows += `<tr class="level-row"><td colspan="7" style="background:#34495e;color:#ecf0f1;font-weight:bold;padding:5px 20px;font-size:10pt;">${level.level_name}</td></tr>`;

        level.classes.forEach(cls => {
          tableRows += `<tr>
            <td style="padding-left:30px;">${cls.class_name} <span style="color:#888;">${cls.series_name}</span></td>
            <td style="text-align:center;">${cls.anciens}</td>
            <td style="text-align:center;">${cls.nouveaux}</td>
            <td style="text-align:center;">${cls.garcons}</td>
            <td style="text-align:center;">${cls.filles}</td>
            <td style="text-align:center;font-weight:bold;">${cls.total}</td>
          </tr>`;
        });

        // Level total
        tableRows += `<tr style="background:#ebedef;font-weight:bold;">
          <td style="padding-left:20px;">Sous-total ${level.level_name}</td>
          <td style="text-align:center;">${level.totals.anciens}</td>
          <td style="text-align:center;">${level.totals.nouveaux}</td>
          <td style="text-align:center;">${level.totals.garcons}</td>
          <td style="text-align:center;">${level.totals.filles}</td>
          <td style="text-align:center;">${level.totals.total}</td>
        </tr>`;
      });

      // Section total
      tableRows += `<tr style="background:#d5dbdb;font-weight:bold;border-top:2px solid #333;">
        <td>Total ${section.section_name}</td>
        <td style="text-align:center;">${section.totals.anciens}</td>
        <td style="text-align:center;">${section.totals.nouveaux}</td>
        <td style="text-align:center;">${section.totals.garcons}</td>
        <td style="text-align:center;">${section.totals.filles}</td>
        <td style="text-align:center;">${section.totals.total}</td>
      </tr>`;
    });

    // Grand total
    tableRows += `<tr style="background:#1a252f;color:white;font-weight:bold;font-size:11pt;">
      <td>TOTAL GENERAL</td>
      <td style="text-align:center;">${filteredGlobal.anciens}</td>
      <td style="text-align:center;">${filteredGlobal.nouveaux}</td>
      <td style="text-align:center;">${filteredGlobal.garcons}</td>
      <td style="text-align:center;">${filteredGlobal.filles}</td>
      <td style="text-align:center;">${filteredGlobal.total}</td>
    </tr>`;

    printWindow.document.write(`<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Statistiques par Cycle - ${school_year}</title>
  <style>
    @page { margin: 10mm 12mm; size: A4 portrait; }
    body { font-family: Arial, sans-serif; font-size: 10pt; color: #333; margin: 0; padding: 0; }
    .header { text-align: center; margin-bottom: 5mm; }
    .header h2 { margin: 0; font-size: 14pt; color: #009B3A; }
    .header h3 { margin: 2mm 0; font-size: 12pt; }
    .header p { margin: 1mm 0; font-size: 9pt; color: #666; }
    .separator { height: 2px; background: linear-gradient(to right, #009B3A 33%, #CE1126 33%, #CE1126 66%, #FCD116 66%); margin: 4mm 30mm; }
    .summary { display: flex; justify-content: space-around; margin: 5mm 0; flex-wrap: wrap; }
    .summary-box { text-align: center; padding: 2mm 6mm; border: 1px solid #ddd; border-radius: 2mm; min-width: 45mm; margin: 1mm; }
    .summary-box .label { font-size: 8pt; color: #666; }
    .summary-box .value { font-size: 16pt; font-weight: bold; }
    .total .value { color: #0d6efd; }
    .anciens .value { color: #198754; }
    .nouveaux .value { color: #fd7e14; }
    .garcons .value { color: #0d6efd; }
    .filles .value { color: #e91e8b; }
    table { width: 100%; border-collapse: collapse; margin-top: 4mm; }
    th { background-color: #1a252f; color: white; padding: 2.5mm 3mm; font-size: 9pt; text-align: center; }
    th:first-child { text-align: left; }
    td { padding: 2mm 3mm; border-bottom: 0.5px solid #ddd; font-size: 9pt; }
    tr:nth-child(even):not(.section-row):not(.level-row) { background-color: #f8f9fa; }
    .footer { text-align: center; margin-top: 6mm; font-size: 8pt; color: #999; border-top: 1px solid #ddd; padding-top: 2mm; }
  </style>
</head>
<body>
  <div class="header">
    <h2>COLLEGE POLYVALENT BILINGUE DE DOUALA</h2>
    <div class="separator"></div>
    <h3>STATISTIQUES DES INSCRIPTIONS PAR CYCLE</h3>
    <p>Annee scolaire : ${school_year} | Filtre : ${filterLabel}</p>
  </div>
  <div class="summary">
    <div class="summary-box total"><div class="label">Total</div><div class="value">${filteredGlobal.total}</div></div>
    <div class="summary-box anciens"><div class="label">Anciens</div><div class="value">${filteredGlobal.anciens}</div></div>
    <div class="summary-box nouveaux"><div class="label">Nouveaux</div><div class="value">${filteredGlobal.nouveaux}</div></div>
    <div class="summary-box garcons"><div class="label">Garcons</div><div class="value">${filteredGlobal.garcons}</div></div>
    <div class="summary-box filles"><div class="label">Filles</div><div class="value">${filteredGlobal.filles}</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th style="text-align:left;">Classe</th>
        <th>Anciens</th>
        <th>Nouveaux</th>
        <th>Garcons</th>
        <th>Filles</th>
        <th>Total</th>
      </tr>
    </thead>
    <tbody>
      ${tableRows}
    </tbody>
  </table>
  <div class="footer">
    Imprime le ${today} - College Polyvalent Bilingue de Douala
  </div>
</body>
</html>`);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-start mb-1 flex-wrap gap-2">
        <div>
          <h4 className="mb-1">
            <PeopleFill className="me-2" />
            Statistiques par Cycle
          </h4>
          <p className="text-muted mb-3">
            Repartition des eleves par cycle et classe - Annee scolaire <strong>{school_year}</strong>
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Form.Select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            style={{ width: '220px' }}
          >
            <option value="all">Tous les cycles</option>
            {sections.map(s => (
              <option key={s.id} value={String(s.id)}>{s.name}</option>
            ))}
          </Form.Select>
          <Button variant="outline-primary" onClick={handlePrint}>
            <PrinterFill className="me-1" /> Imprimer
          </Button>
        </div>
      </div>

      {/* Global Stats Cards */}
      <Row className="mb-4 g-3">
        <Col xs={6} lg>
          <Card className="shadow-sm border-0 h-100" style={{ borderLeft: '4px solid #0d6efd' }}>
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#e7f1ff' }}>
                <PeopleFill size={24} color="#0d6efd" />
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Total</div>
                <div className="fw-bold" style={{ fontSize: '1.5rem', lineHeight: 1 }}>{filteredGlobal.total}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} lg>
          <Card className="shadow-sm border-0 h-100" style={{ borderLeft: '4px solid #198754' }}>
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#e8f5e9' }}>
                <PersonCheckFill size={24} color="#198754" />
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Anciens</div>
                <div className="fw-bold" style={{ fontSize: '1.5rem', lineHeight: 1 }}>{filteredGlobal.anciens}</div>
                <small className="text-success">{pctAnciens}%</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} lg>
          <Card className="shadow-sm border-0 h-100" style={{ borderLeft: '4px solid #fd7e14' }}>
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#fff3e0' }}>
                <PersonPlusFill size={24} color="#fd7e14" />
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Nouveaux</div>
                <div className="fw-bold" style={{ fontSize: '1.5rem', lineHeight: 1 }}>{filteredGlobal.nouveaux}</div>
                <small className="text-warning">{pctNouveaux}%</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} lg>
          <Card className="shadow-sm border-0 h-100" style={{ borderLeft: '4px solid #0dcaf0' }}>
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#e0f7fa' }}>
                <GenderMale size={24} color="#0dcaf0" />
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Garcons</div>
                <div className="fw-bold" style={{ fontSize: '1.5rem', lineHeight: 1 }}>{filteredGlobal.garcons}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} lg>
          <Card className="shadow-sm border-0 h-100" style={{ borderLeft: '4px solid #e91e8b' }}>
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#fce4ec' }}>
                <GenderFemale size={24} color="#e91e8b" />
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Filles</div>
                <div className="fw-bold" style={{ fontSize: '1.5rem', lineHeight: 1 }}>{filteredGlobal.filles}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Progress Bar */}
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between mb-2">
            <span><Badge bg="success" className="me-1">Anciens</Badge> {filteredGlobal.anciens}</span>
            <span><Badge bg="warning" text="dark" className="me-1">Nouveaux</Badge> {filteredGlobal.nouveaux}</span>
          </div>
          <ProgressBar style={{ height: '22px' }}>
            <ProgressBar variant="success" now={parseFloat(pctAnciens)} label={`${pctAnciens}%`} key={1} />
            <ProgressBar variant="warning" now={parseFloat(pctNouveaux)} label={`${pctNouveaux}%`} key={2} />
          </ProgressBar>
        </Card.Body>
      </Card>

      {/* Table by Section/Level/Class */}
      {filteredSections.map(section => (
        <Card className="shadow-sm mb-4" key={section.section_id}>
          <Card.Header className="text-white" style={{ backgroundColor: '#2c3e50' }}>
            <h5 className="mb-0">{section.section_name}
              <Badge bg="light" text="dark" className="ms-2">{section.totals.total} eleves</Badge>
            </h5>
          </Card.Header>
          <Card.Body className="p-0">
            <Table striped hover responsive className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th>Classe</th>
                  <th className="text-center">Anciens</th>
                  <th className="text-center">Nouveaux</th>
                  <th className="text-center">G</th>
                  <th className="text-center">F</th>
                  <th className="text-center">Total</th>
                  <th style={{ width: '18%' }}>Repartition</th>
                </tr>
              </thead>
              <tbody>
                {section.levels.map(level => (
                  <React.Fragment key={level.level_id}>
                    {/* Level header */}
                    <tr style={{ backgroundColor: '#ebedef' }}>
                      <td colSpan={7} className="fw-bold" style={{ paddingLeft: '12px' }}>
                        <FunnelFill className="me-2" size={14} />
                        {level.level_name}
                      </td>
                    </tr>

                    {/* Classes */}
                    {level.classes.map((cls, idx) => {
                      const pA = cls.total > 0 ? ((cls.anciens / cls.total) * 100).toFixed(0) : 0;
                      const pN = cls.total > 0 ? ((cls.nouveaux / cls.total) * 100).toFixed(0) : 0;
                      return (
                        <tr key={idx}>
                          <td style={{ paddingLeft: '28px' }}>
                            <strong>{cls.class_name}</strong>{' '}
                            <span className="text-muted">{cls.series_name}</span>
                          </td>
                          <td className="text-center"><Badge bg="success" pill>{cls.anciens}</Badge></td>
                          <td className="text-center"><Badge bg="warning" text="dark" pill>{cls.nouveaux}</Badge></td>
                          <td className="text-center"><Badge bg="info" pill>{cls.garcons}</Badge></td>
                          <td className="text-center"><Badge bg="danger" pill>{cls.filles}</Badge></td>
                          <td className="text-center fw-bold">{cls.total}</td>
                          <td>
                            <ProgressBar style={{ height: '16px' }}>
                              <ProgressBar variant="success" now={parseFloat(pA)} key={1} />
                              <ProgressBar variant="warning" now={parseFloat(pN)} key={2} />
                            </ProgressBar>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Level total */}
                    <tr style={{ backgroundColor: '#d5dbdb', fontWeight: 'bold' }}>
                      <td style={{ paddingLeft: '12px' }}>Sous-total {level.level_name}</td>
                      <td className="text-center">{level.totals.anciens}</td>
                      <td className="text-center">{level.totals.nouveaux}</td>
                      <td className="text-center">{level.totals.garcons}</td>
                      <td className="text-center">{level.totals.filles}</td>
                      <td className="text-center">{level.totals.total}</td>
                      <td></td>
                    </tr>
                  </React.Fragment>
                ))}

                {/* Section total */}
                <tr className="table-dark fw-bold">
                  <td>Total {section.section_name}</td>
                  <td className="text-center">{section.totals.anciens}</td>
                  <td className="text-center">{section.totals.nouveaux}</td>
                  <td className="text-center">{section.totals.garcons}</td>
                  <td className="text-center">{section.totals.filles}</td>
                  <td className="text-center">{section.totals.total}</td>
                  <td></td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ))}

      {filteredSections.length === 0 && (
        <Alert variant="info" className="text-center">
          Aucune donnee disponible pour ce filtre.
        </Alert>
      )}
    </Container>
  );
}

export default StatistiquesCycle;
